import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import { fetchCarrierAccessDetail, mapCarrierAccessApiItemToCarrierAccessDetail } from "../requests/request";
import type { CarrierAccessDetail } from "../carrierAccess.types";

/**
 * Carrier Access — detail data hook (use<Service>Details convention).
 * Resolves the id from a route param (`/details/:id`) or a `?id=` query param,
 * so it works with both routing styles used across products.
 */
export function useCarrierAccessDetails() {
  const axiosAuth = useAxiosAuth0();
  const { tenantId } = useAppSelector((state) => state.selection);
  const params = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();

  const rawId = params.id ?? searchParams.get("id");
  const id = rawId != null ? Number.parseInt(rawId, 10) : NaN;

  const [detail, setDetail] = useState<CarrierAccessDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (tenantId == null || Number.isNaN(id)) {
        setLoading(false);
        setError(Number.isNaN(id) ? "Missing or invalid id." : null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetchCarrierAccessDetail(axiosAuth, tenantId, id, signal);
        if (signal?.aborted) return;
        const payload = response.data?.data;
        if (response.data?.success === false || !payload) {
          setDetail(null);
          setError(response.data?.message || "Carrier Access not found.");
          return;
        }
        setDetail(mapCarrierAccessApiItemToCarrierAccessDetail(payload));
      } catch (err) {
        if (signal?.aborted) return;
        setError("Failed to load details. Please try again.");
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [axiosAuth, tenantId, id]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  /** Apply a saved edit locally, so a rename does not refetch the legs. */
  const patch = useCallback(
    (changes: Partial<CarrierAccessDetail>) =>
      setDetail((current) => (current ? { ...current, ...changes } : current)),
    []
  );

  return { id, detail, loading, error, refetch: () => load(), patch };
}
